// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ContentChild,
  ElementRef,
  Input,
  TemplateRef,
} from '@angular/core';
import { Observable, combineLatest, map, startWith, take, tap } from 'rxjs';
import { APICallState } from '../utils/api-call-state.util';
import { Observe } from '../utils/as-observable';

export enum ErrorBehaviors {
  Retry = 0,
  Dialog = 1,
  Reload = 2,
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GenericApiCall = APICallState<any, any>;

@Component({
  selector: 'api-call-gate',
  styleUrls: ['api-call-gate.component.scss'],
  template: `
    <ng-container *ngIf="loadingButNotRetrying$ | async; else notLoading">
      <ng-container *ngIf="!!loading; else loadingDefault">
        <ng-container *ngTemplateOutlet="loading"></ng-container>
      </ng-container>
      <ng-template #loadingDefault>
        <sh-spinner [label]="loadingLabel"></sh-spinner>
      </ng-template>
    </ng-container>
    <ng-template #notLoading>
      <ng-container *ngIf="error$ | async as error_; else noError">
        <ng-container
          *ngTemplateOutlet="
            !!error ? error : errorDefault;
            context: { $implicit: error_, rawError: rawError$ | async }
          "
        ></ng-container>
      </ng-container>
      <ng-template #noError>
        <ng-container *ngIf="dataFired$ | async; else initial_">
          <ng-container *ngIf="lastSuccessfulData">
            <ng-container
              *ngTemplateOutlet="
                lastSuccessfulData;
                context: { $implicit: lastSuccessfulData$ | async }
              "
            ></ng-container>
          </ng-container>
        </ng-container>

        <ng-template #initial_>
          <ng-container
            *ngTemplateOutlet="!!initial ? initial : undefined"
          ></ng-container>
        </ng-template>
      </ng-template>
    </ng-template>

    <ng-template #errorDefault let-error>
      <ng-container *ngIf="errorBehaviors.indexOf(1) !== -1">
        <dialog *ngIf="errorDialogOpen">
          <sh-card type="error" visible style="max-width: 80%">
            <h3>
              <sh-icon
                size="s"
                icon="error"
                style="display: inline-block"
              ></sh-icon>
              Error
            </h3>
            <div [innerHtml]="error?.toString() || 'Unknown Error'"></div>
            <sh-button
              slot="footer"
              label="Close"
              (click)="errorDialogOpen = false"
            ></sh-button>
          </sh-card>
        </dialog>
      </ng-container>
      <ng-container
        *ngIf="
          errorBehaviors.indexOf(0) !== -1 || errorBehaviors.indexOf(2) !== -1
        "
      >
        <div class="error">
          <h3>
            <sh-icon
              size="l"
              icon="error"
              style="display: inline-block"
            ></sh-icon>
          </h3>
          <div [innerHtml]="error?.toString() || 'Unknown Error'"></div>
          <sh-button
            *ngIf="errorBehaviors.indexOf(0) !== -1"
            slot="footer"
            size="auto"
            color="secondary"
            icon="refresh"
            icon-position="left"
            label="Try again"
            (click)="retry()"
          ></sh-button>
          <sh-button
            *ngIf="errorBehaviors.indexOf(2) !== -1"
            slot="footer"
            size="auto"
            color="tertiary"
            icon="refresh"
            icon-position="left"
            label="Hard reload"
            (click)="reload()"
          ></sh-button>
        </div>
      </ng-container>
    </ng-template>
  `,
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [NgIf, NgTemplateOutlet, AsyncPipe],
})
export class APICallGateComponent {
  /**
   * api call used to mange template (can also take an array of calls)
   */
  @Input({ required: true }) apiCall!: GenericApiCall | GenericApiCall[];

  /**
   * loadingLabel
   */
  @Input() loadingLabel = 'loading..';

  /**
   * forceLoading
   * helpful for debugging
   */
  @Input() forceLoading = false;
  @Observe('forceLoading') forceLoading$!: Observable<typeof this.forceLoading>;

  /**
   * show popover or retry button
   */
  @Input() errorBehaviors: ErrorBehaviors[] = [
    ErrorBehaviors.Retry,
    ErrorBehaviors.Reload,
  ];

  /**
   * template to show in before any request is made
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ContentChild('initial') initial?: TemplateRef<any>;

  /**
   * template to show while loading
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ContentChild('loading') loading?: TemplateRef<any>;

  /**
   * template to show in case of error
   * context is $implicit: [convenienceError], rawError: raw error
   * example:
   * <ng-template #error let-error let-raw="rawError">
   *   <!-- error is something you can render right away -->
   *   <-- raw is the first raw HTTPErrorResponse -->
   * </ng-template>
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ContentChild('error') error?: TemplateRef<any>;

  /**
   * template to show in case of success
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ContentChild('lastSuccessfulData') lastSuccessfulData?: TemplateRef<any>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ContentChild('data') data?: TemplateRef<any>;

  /**
   * transient state
   */
  errorDialogOpen = true;

  constructor(private host: ElementRef) {}

  /**
   * retry failed calls
   */
  reload(): void {
    window.location.reload();
  }

  /**
   * retry failed calls
   */
  retry(): void {
    this.callArray.forEach((call) => {
      call.error$.pipe(take(1)).subscribe((error) => {
        if (error) {
          call.retry();
        }
      });
    });
  }

  /**
   * helpers
   */
  get callArray(): GenericApiCall[] {
    return (
      Array.isArray(this.apiCall)
        ? (this.apiCall as GenericApiCall[])
        : [this.apiCall]
    ).filter((call) => !!call);
  }

  // true as long as any api call is still loading
  get loading$(): Observable<boolean> {
    return combineLatest([
      ...this.callArray.map((call) => call.loading$.pipe(startWith(true))),
      this.forceLoading$,
    ]).pipe(
      map((loading) => loading.some((l) => l)),
      tap((loading) => {
        if (loading) {
          // used to apply slightly different layouts when loading or rendering children!
          this.host.nativeElement.removeAttribute('loaded');
        }
      })
    );
  }

  // true while reqtry is in progress
  get retrying$(): Observable<boolean> {
    return combineLatest(
      this.callArray.map((call) => call.retrying$.pipe(startWith(false)))
    ).pipe(map((retrying) => retrying.every((l) => l)));
  }

  get loadingButNotRetrying$(): Observable<boolean> {
    return combineLatest([this.loading$, this.retrying$]).pipe(
      map(([loading, retrying]) => {
        return loading && !retrying;
      })
    );
  }

  get rawError$(): Observable<unknown> {
    return combineLatest(
      this.callArray.map((call) => call.error$.pipe(startWith(undefined)))
    ).pipe(
      map((errors) => {
        return errors.find((e) => !!e);
      })
    );
  }

  // picks any error in case there are some
  get error$(): Observable<unknown> {
    return this.rawError$.pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((e: any) => {
        // unpack error
        if (e) {
          const opId = e?.error?.operationId;
          const postFix = opId ? `<i>(OpID: ${opId})</i>` : '';
          if (e.statusText && e.status) {
            return `<b>${e.status}: </b> ${e.statusText}${postFix}`;
          }
          if (e.message) {
            return `${e.message}${postFix}`;
          }
          if (e.statusText) {
            return `${e.statusText}${postFix}`;
          }
          return e;
        }
        return e;
      })
    );
  }

  // returns array of data or unpacked first element in case only one call was given
  get lastSuccessfulData$(): Observable<unknown | unknown[]> {
    return combineLatest(
      this.callArray.map((call) => call.lastSuccessfulData$)
    ).pipe(
      map((data) => (data.length === 1 ? data[0] : data)),
      tap((data) => {
        if (data) {
          // used to apply slightly different layouts when loading or rendering children!
          this.host.nativeElement.setAttribute('loaded', '');
        }
      })
    );
  }

  get dataFired$(): Observable<boolean> {
    return this.lastSuccessfulData$.pipe(map(() => true));
  }
}
